import { supabase } from '@/lib/supabase';
import { parseValorCentavos } from '@/pages/ingresso/utils';

// ---------------------------------------------------------------------------
// Taxa de serviço ANTES do pagamento. Mesma fonte que a tela de compra usa:
// a função resumo_cobranca() do banco (nunca calculamos preço na tela).
// Quando o organizador absorve a taxa (quem_paga_taxa = 'organizador'), o
// comprador paga só o valor do ingresso — e nada de "+ taxa" aparece.
// ---------------------------------------------------------------------------

export interface TaxaValor {
  baseCentavos: number;
  taxaCentavos: number;
  totalCentavos: number;
}

/** true quando o organizador absorve a taxa (o comprador não paga a mais). */
export function absorveTaxa(quemPaga: string | null | undefined): boolean {
  return (quemPaga ?? '').trim().toLowerCase() === 'organizador';
}

/** Taxa de uma modalidade/opção, direto de resumo_cobranca. */
export async function buscarTaxa(
  modalidadeId: number,
  opcaoId: number | null = null,
): Promise<TaxaValor | null> {
  try {
    const { data, error } = await supabase.rpc('resumo_cobranca', {
      p_modalidade_id: modalidadeId,
      p_opcao_id: opcaoId,
    });
    if (error) return null;
    const linhas =
      (data as { linha: string; valor: string; destaque: boolean }[] | null) ?? [];
    const total = linhas.find((l) => l.destaque);
    if (!total) return null;
    const totalCentavos = parseValorCentavos(total.valor);
    const base = linhas.find((l) => l.linha === 'Ingresso');
    const baseCentavos = base ? parseValorCentavos(base.valor) : totalCentavos;
    return {
      baseCentavos,
      taxaCentavos: Math.max(0, totalCentavos - baseCentavos),
      totalCentavos,
    };
  } catch {
    return null;
  }
}

/** Quem paga a taxa, por evento (para a vitrine). Só leitura. */
export async function eventosComTaxa(ids: number[]): Promise<Set<number>> {
  const unicos = Array.from(new Set(ids.filter((i) => Number.isFinite(i))));
  if (unicos.length === 0) return new Set();
  try {
    const { data, error } = await supabase
      .from('eventos')
      .select('id, quem_paga_taxa')
      .in('id', unicos);
    if (error) return new Set();
    const comTaxa = new Set<number>();
    ((data as { id: number; quem_paga_taxa: string | null }[] | null) ?? []).forEach((e) => {
      if (!absorveTaxa(e.quem_paga_taxa)) comTaxa.add(e.id);
    });
    return comTaxa;
  } catch {
    return new Set();
  }
}